import { Check, ClipboardCopy, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const Value = ({ step, label, value, onCopy }) => {
  const long = String(value).length > 60 || String(value).includes("\n");
  return (
    <div className="rounded-lg border border-ink-600 bg-ink-900/70 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
          <p className={`mt-1 break-words text-slate-200 ${long ? "whitespace-pre-wrap text-xs" : "font-mono text-[11px]"}`}>
            {value}
          </p>
        </div>
        <Button data-testid={`play-copy-${step}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          variant="ghost" size="sm" onClick={() => onCopy(value, label)}
          className="shrink-0 text-slate-400 hover:text-gold">
          <ClipboardCopy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

/** The Play Console run for one app: every value pre-filled, every step remembered. */
export const PlayChecklist = ({ app, steps, checklist, onTick, onCopy, busy }) => {
  const done = steps.filter((s) => checklist[s.key]).length;
  return (
    <div data-testid="play-checklist" className="rounded-xl border border-gold/30 bg-ink-800 p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-display text-lg text-slate-100">
            {app.platform === "ios" ? "Publish to the App Store" : "Publish to Google Play"}
          </p>
          <p className="mt-1 text-sm text-slate-400">{app.app_name} · {app.app_id}</p>
        </div>
        <p data-testid="play-progress" className="font-mono text-[11px] uppercase tracking-widest text-gold">
          {done} of {steps.length} done
        </p>
      </div>
      <div className="mt-3 h-1 overflow-hidden rounded-full bg-ink-600">
        <div className="h-full bg-gold transition-all duration-500"
          style={{ width: `${Math.round((done / steps.length) * 100)}%` }} />
      </div>

      <ol className="mt-5 space-y-3">
        {steps.map((step, index) => {
          const ticked = Boolean(checklist[step.key]);
          return (
            <li key={step.key} data-testid={`play-step-${step.key}`}
              className={`rounded-lg border p-4 transition-colors ${
                ticked ? "border-emerald-500/30 bg-emerald-500/5" : "border-ink-600 bg-ink-900/40"}`}>
              <div className="flex items-start gap-3">
                <button type="button" data-testid={`play-tick-${step.key}`} disabled={busy}
                  onClick={() => onTick(step.key, !ticked)}
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors ${
                    ticked ? "border-emerald-400 bg-emerald-400 text-ink-900"
                           : "border-ink-600 text-slate-500 hover:border-gold hover:text-gold"}`}>
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" />
                        : ticked ? <Check className="h-3.5 w-3.5" />
                        : <span className="font-mono text-[11px]">{index + 1}</span>}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${ticked ? "text-slate-400 line-through" : "text-slate-100"}`}>
                    {step.title}
                    {step.state === "todo" && (
                      <span data-testid={`play-step-todo-${step.key}`} className="ml-2 font-mono text-[10px] uppercase tracking-widest text-amber-400">
                        not ready yet
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-400">{step.body}</p>
                  {step.link && (
                    <a data-testid={`play-link-${step.key}`} href={step.link} target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1 font-mono text-[11px] text-gold hover:underline">
                      {step.link.replace(/^https?:\/\//, "").split("/")[0]}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {Boolean(step.copy?.length) && (
                    <div className="mt-3 space-y-2">
                      {step.copy.map(([label, value]) => (
                        <Value key={label} step={step.key} label={label} value={value} onCopy={onCopy} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
